"use client";

import { useCallback, useState, type ReactNode } from 'react'
import { fmtDate, fmtDelta, fmtUsdCents, fmtUsdCompact } from '@/lib/finance/format'
import { groupColor, groupOf } from '@/lib/finance/categories'
import type { Txn, Account } from '@/lib/finance/types'
import { txnAccountLabel } from '@/lib/finance/accountResolve'
import { tagExcluded, type TagPolicy } from '@/lib/finance/finance-data-context'

export function Card({
  title,
  subtitle,
  actions,
  children,
  className = '',
}: {
  title?: string
  subtitle?: string
  actions?: ReactNode
  children: ReactNode
  className?: string
}) {
  return (
    <section className={`rounded-2xl border border-edge bg-surface-1 p-4 sm:p-5 ${className}`}>
      {(title || actions) && (
        <header className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
          <div>
            {title && <h2 className="text-sm font-semibold text-ink">{title}</h2>}
            {subtitle && <p className="mt-0.5 text-xs text-ink-3">{subtitle}</p>}
          </div>
          {actions}
        </header>
      )}
      {children}
    </section>
  )
}

/** Stat tile: label · value · optional delta vs a named period · sparkline. */
export function StatTile({
  label,
  value,
  delta,
  deltaLabel,
  upIsGood = true,
  spark,
}: {
  label: string
  value: string
  delta?: number
  deltaLabel?: string
  upIsGood?: boolean
  spark?: number[]
}) {
  const good = delta !== undefined && (delta >= 0) === upIsGood
  return (
    <div className="rounded-2xl border border-edge bg-surface-1 p-4">
      <div className="text-xs text-ink-2">{label}</div>
      <div className="mt-1 text-2xl font-semibold text-ink">{value}</div>
      {delta !== undefined && (
        <div className={`mt-0.5 text-xs font-medium ${good ? 'text-pos' : 'text-neg'}`}>
          {fmtDelta(delta)}
          {deltaLabel && <span className="ml-1 font-normal text-ink-3">{deltaLabel}</span>}
        </div>
      )}
      {spark && spark.length > 1 && <Sparkline data={spark} className="mt-2" />}
    </div>
  )
}

/**
 * 12-point balance sparkline. Balances are plotted as magnitudes so every
 * account — assets and liabilities (stored negative) alike — reads as a
 * positive line. The end-bullet color conveys direction on the raw signed
 * value: green when the balance is money you have (asset positive, liability
 * paid off), red when it's money owed or overdrawn (asset negative, liability
 * carrying a balance) — so a credit card / loan gets a red dot on a positive
 * line, the inverse of a healthy debit account.
 */
export function Sparkline({ data, className = '' }: { data: (number | null)[]; className?: string }) {
  const w = 120
  const h = 28
  const n = data.length
  // Points are positioned by their index across the WHOLE range, so an account
  // that opened partway through starts partway across (leading nulls = blank).
  const defined = data.filter((v): v is number => v != null).map(Math.abs)
  if (defined.length < 2) return null
  const min = Math.min(...defined)
  const max = Math.max(...defined)
  const span = max - min || 1
  const pts = data.map((v, i) =>
    v == null
      ? null
      : ([(i / (n - 1)) * (w - 8) + 4, h - 4 - ((Math.abs(v) - min) / span) * (h - 8)] as [number, number]),
  )
  let path = ''
  let started = false
  for (const p of pts) {
    if (!p) continue
    path += `${started ? 'L' : 'M'}${p[0].toFixed(1)},${p[1].toFixed(1)}`
    started = true
  }
  // Last observed point → bullet (sign-colored on the raw signed value).
  let lastRaw = 0
  let lastPt: [number, number] | null = null
  for (let i = 0; i < n; i++) {
    if (data[i] != null) {
      lastRaw = data[i] as number
      lastPt = pts[i]
    }
  }
  const bullet = lastRaw >= 0 ? 'var(--pos)' : 'var(--neg)'
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className={`h-7 w-30 ${className}`} aria-hidden role="presentation">
      <path d={path} fill="none" stroke="var(--series-other)" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
      {lastPt && <circle cx={lastPt[0]} cy={lastPt[1]} r={3.5} fill={bullet} stroke="var(--surface-1)" strokeWidth={2} />}
    </svg>
  )
}

/** Signed currency in pos/neg ink; plain ink for zero. Always shows an explicit
 * +/- sign (fmtUsdCompact already carries '-', so we only prepend '+'). */
export function Money({ value, compact = false }: { value: number; compact?: boolean }) {
  const cls = value > 0 ? 'text-pos' : value < 0 ? 'text-neg' : 'text-ink'
  const sign = value > 0 ? '+' : ''
  const text = compact ? sign + fmtUsdCompact(value) : sign + fmtUsdCents(value)
  return <span className={`font-medium tabular-nums ${cls}`}>{text}</span>
}

/** Small color key: a colored line/swatch beside secondary-ink text. */
export function LegendKey({
  color,
  label,
  shape = 'rect',
}: {
  color: string
  label: string
  shape?: 'rect' | 'line'
}) {
  return (
    <span className="inline-flex items-center gap-1.5 text-xs text-ink-2">
      {shape === 'rect' ? (
        <span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ background: color }} />
      ) : (
        <span className="inline-block h-0.5 w-3.5 rounded-full" style={{ background: color }} />
      )}
      {label}
    </span>
  )
}

/**
 * Pill — the single chip used for overlay toggles, category/group hops, and
 * in-page jump links, so these controls stay visually identical everywhere
 * (no per-page skew). Renders a <button> (toggle) or an <a> (when `href` is
 * set, for anchor jumps). Active = filled; inactive = outlined. Pass `color`
 * to show a LegendKey swatch (line or rect) before the label.
 */
export function Pill({
  label,
  color,
  shape = 'rect',
  active = false,
  fixed = false,
  size = 'sm',
  onClick,
  href,
}: {
  label: string
  color?: string
  shape?: 'rect' | 'line'
  active?: boolean
  /** Always-on, non-interactive pill (e.g. a series that can't be toggled off).
   * Rendered as a static chip with a dashed edge + default cursor so it reads
   * as "shown, not clickable" next to the toggleable ones. */
  fixed?: boolean
  /** 'md' matches the height of the filter <select>s it sits beside. */
  size?: 'sm' | 'md'
  onClick?: () => void
  href?: string
}) {
  const sizeCls = size === 'md' ? 'px-3 py-1.5 text-sm' : 'px-2.5 py-1 text-xs'
  const className = `inline-flex items-center rounded-full border font-medium transition-colors ${sizeCls} ${
    active ? 'border-transparent bg-surface-3 text-ink' : 'border-edge text-ink-2 hover:bg-surface-3/60'
  }`
  const content = color ? <LegendKey color={color} label={label} shape={shape} /> : label
  if (fixed) {
    return (
      <span
        aria-disabled="true"
        title="Always shown"
        className="inline-flex cursor-default items-center rounded-full border border-dashed border-edge bg-surface-2/60 px-2.5 py-1 text-xs font-medium text-ink"
      >
        {content}
      </span>
    )
  }
  return href ? (
    <a href={href} className={className}>
      {content}
    </a>
  ) : (
    <button type="button" onClick={onClick} aria-pressed={active} className={className}>
      {content}
    </button>
  )
}

/** One-row segmented control (time ranges, view toggles). An option may carry
 * its own className — e.g. `hidden sm:block` to hide a chip on mobile. */
export function Segmented<T extends string>({
  options,
  value,
  onChange,
  ariaLabel,
}: {
  options: { value: T; label: string; className?: string }[]
  value: T
  onChange: (v: T) => void
  ariaLabel: string
}) {
  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      className="inline-flex max-w-full items-center gap-0.5 overflow-x-auto rounded-full border border-edge bg-surface-1 p-0.5"
    >
      {options.map((o) => (
        <button
          key={o.value}
          role="tab"
          aria-selected={o.value === value}
          onClick={() => onChange(o.value)}
          className={`shrink-0 rounded-full px-3 py-1 text-xs font-medium transition-colors ${
            o.value === value
              ? 'bg-surface-3 text-ink'
              : 'text-ink-2 hover:bg-surface-3/60 hover:text-ink'
          } ${o.className ?? ''}`}
        >
          {o.label}
        </button>
      ))}
    </div>
  )
}

/**
 * Page title row (h1 + range picker/actions). Sticky on desktop only, so the
 * date picker stays reachable while scrolling long pages; on mobile it scrolls
 * away normally (the top app bar is already sticky there). The negative
 * margins re-span the main content padding so stuck content can't peek out
 * above, and the translucent page background keeps it legible.
 */
export function PageHeader({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 lg:sticky lg:top-0 lg:z-30 lg:-mx-4 lg:-mt-6 lg:bg-surface-2/95 lg:px-4 lg:py-3 lg:backdrop-blur">
      <h1 className="text-xl font-semibold tracking-tight">{title}</h1>
      {children && <div className="flex flex-wrap items-center gap-2">{children}</div>}
    </div>
  )
}

/** Set-backed toggle state — the show/hide pattern behind every legend. */
export function useToggleSet<T>(initial?: Iterable<T>): [Set<T>, (v: T) => void] {
  const [set, setSet] = useState<Set<T>>(() => new Set(initial))
  const toggle = useCallback((v: T) => {
    setSet((prev) => {
      const next = new Set(prev)
      if (next.has(v)) next.delete(v)
      else next.add(v)
      return next
    })
  }, [])
  return [set, toggle]
}

/**
 * Wraps a chart legend row / band with a two-action scope popover shown on
 * hover. "Only this" narrows the category scope to this item; "Exclude" drops
 * it. The menu is a DOM child of the wrapper (absolutely positioned under it),
 * so the wrapper's mouseleave only fires once the pointer leaves BOTH the
 * trigger and the menu — moving down onto the menu keeps it open, no gap/flicker.
 */
export function ScopeMenu({
  children,
  onOnly,
  onExclude,
  className = 'relative inline-flex',
}: {
  children: ReactNode
  onOnly: () => void
  onExclude: () => void
  className?: string
}) {
  const [open, setOpen] = useState(false)
  return (
    <span className={className} onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      {children}
      {open && (
        <span className="absolute left-0 top-full z-20 pt-1">
          <span className="flex overflow-hidden rounded-lg border border-edge bg-surface-1 text-[11px] font-medium shadow-lg">
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onOnly() }}
              className="whitespace-nowrap px-2.5 py-1 text-ink-2 hover:bg-surface-3 hover:text-ink"
            >
              Only this
            </button>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onExclude() }}
              className="whitespace-nowrap border-l border-edge px-2.5 py-1 text-ink-2 hover:bg-surface-3 hover:text-ink"
            >
              Exclude
            </button>
          </span>
        </span>
      )}
    </span>
  )
}

/** "Reset filters" chip — rendered by a filter row only while something
 * deviates from the page's defaults (all categories, all accounts, default
 * tag policy). One tap restores the defaults. */
export function ResetFiltersChip({ onReset }: { onReset: () => void }) {
  return (
    <button
      type="button"
      onClick={onReset}
      className="inline-flex items-center gap-1 rounded-full border border-warn/40 bg-warn/10 px-3 py-1.5 text-xs font-medium text-warn hover:bg-warn/20"
    >
      <svg viewBox="0 0 20 20" className="h-3 w-3" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden>
        <path d="M6 6l8 8M14 6l-8 8" strokeLinecap="round" />
      </svg>
      Reset filters
    </button>
  )
}

/** Legend chip that hides/shows its series — dimmed when hidden. One look for
 * every hideable legend (Trends stack, composition charts, projections). */
export function LegendToggle({
  label,
  color,
  shape = 'rect',
  hidden,
  onToggle,
}: {
  label: string
  color: string
  shape?: 'rect' | 'line'
  hidden: boolean
  onToggle: () => void
}) {
  return (
    <button
      onClick={onToggle}
      aria-pressed={!hidden}
      className={`inline-flex items-center rounded-full border px-2.5 py-1 text-xs transition-colors ${
        hidden ? 'border-transparent opacity-40 hover:opacity-70' : 'border-edge bg-surface-3/50'
      }`}
    >
      <LegendKey color={color} label={label} shape={shape} />
    </button>
  )
}

/** Signed transaction amount: +$12.34 in pos ink, muted when the txn doesn't
 * count toward budgeting. */
export function TxnAmount({ t, muted = false, title }: { t: Txn; muted?: boolean; title?: string }) {
  const cls = muted ? 'text-ink-3' : t.amount > 0 ? 'text-pos' : 'text-ink'
  return (
    <span className={`shrink-0 text-sm font-medium tabular-nums ${cls}`} title={title}>
      {t.amount > 0 ? '+' : ''}
      {fmtUsdCents(t.amount)}
    </span>
  )
}

/** One badge per tag on the row. Excluded tags (hidden from budgeting under the
 * current policy) render in warn; included tags stay neutral. Shared by the
 * Transactions table and every TxnListItem list (Overview, AccountDetail) — a
 * tag that changes what counts toward budgeting shouldn't be visible on only
 * one page. */
export function TagBadges({ tags, policy }: { tags?: string[]; policy: TagPolicy }) {
  if (!tags?.length) return null
  return (
    <>
      {tags.map((tag) => {
        const excl = tagExcluded(tag, policy)
        return (
          <span
            key={tag}
            className={`ml-1 shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-medium ${
              excl ? 'bg-warn/15 text-warn' : 'bg-surface-3 text-ink-3'
            }`}
            title={excl ? `${tag} — hidden from budgeting` : `${tag} — counted in budgeting`}
          >
            {tag}
          </span>
        )
      })}
    </>
  )
}

/** The one transaction list row: description / group-colored dot / category ·
 * date (· account) / signed amount. Used by Overview, Trends drilldowns,
 * AccountDetail, and the Transactions mobile view. */
export function TxnListItem({
  t,
  showAccount = false,
  muted = false,
  badge,
  accounts,
}: {
  t: Txn
  showAccount?: boolean
  muted?: boolean
  badge?: ReactNode
  /** When showing the account, pass the roster to render the linked entity's
   * canonical label (owner-disambiguated) instead of the raw Empower string. */
  accounts?: Account[]
}) {
  return (
    <li className="flex items-center justify-between gap-3 py-2.5">
      <div className="min-w-0">
        <div className="truncate text-sm font-medium">{t.description}</div>
        <div className="mt-0.5 flex items-center gap-1.5 text-xs text-ink-3">
          <span
            className="inline-block h-2 w-2 shrink-0 rounded-full"
            style={{ background: groupColor(groupOf(t.category)) }}
          />
          <span className="truncate">
            {t.category} · {fmtDate(t.date)}
            {showAccount ? ` · ${accounts ? txnAccountLabel(t, accounts) : t.accountId}` : ''}
          </span>
          {/* Badges ride inline only where there's room. On phones they'd
              squeeze the category · date · account text down to "Service
              Char…", so below `sm` they move to their own row instead. */}
          <span className="hidden sm:contents">{badge}</span>
        </div>
        {/* empty:hidden collapses the row (margin included) when the badge
            slot renders nothing — untagged rows keep their exact height. */}
        <div className="-ml-1 mt-1 flex flex-wrap items-center gap-y-1 empty:hidden sm:hidden">{badge}</div>
      </div>
      <TxnAmount t={t} muted={muted} />
    </li>
  )
}

